import Link from "next/link";
import type { NavNode } from "@/lib/treatments";
import type { BlogPost } from "@/lib/blog";
import { useI18n } from "./I18nContext";

// The .sidebar-box column from the treatment PHP pages: doctor card, treatment
// links and recent posts. It was byte-identical on every page, so it lives here
// rather than in the CMS — except the treatment links and recent posts, which
// are now generated instead of hand-maintained lists of hardcoded URLs.

// Kept as one string so it hashes to a single translation-memory entry rather
// than fragmenting across JSX line breaks. Ends mid-sentence exactly as the
// original PHP did.
const DOCTOR_BLURB =
  "Dr. Parveen Yadav is Chief & Senior Consultant in Thoracic Surgery and Surgical Oncology at " +
  "Artemis Hospital, Gurgaon. With 18+ years’ experience, he specializes in minimally invasive " +
  "and robotic lung and esophageal surgeries, and has performed 5,700+ major thoracic procedure";

export default function TreatmentSidebar({
  treatments,
  recentPosts,
}: {
  treatments: NavNode[];
  recentPosts: BlogPost[];
}) {
  const { t, localizeHref } = useI18n();
  return (
    <div className="sidebar-box p-3">
      <div className="doctor-card text-center mb-4">
        <img src="/assets/img/drim.webp" className="img-fluid rounded mb-3" alt="Dr. Parveen Yadav" />
        <h5 className="fw-bold text-white mb-1">Dr. Parveen Yadav</h5>
        <p className="small text-light mb-3">{t(DOCTOR_BLURB)}</p>
        <Link href={localizeHref("/about")} className="btn btn-light btn-sm fw-bold px-3 mb-2">
          {t("Read More")}
        </Link>
        <Link href={localizeHref("/contact")} className="btn btn-primary btn-sm fw-bold px-3 mb-2">
          {t("Make Appointment")}
        </Link>
      </div>

      <div className="mb-4">
        <h6 className="text-white fw-bold mb-3">{t("Our Treatments")}</h6>
        <div className="treatment-box">
          {treatments.map((item) => (
            <Link key={item.href} href={localizeHref(item.href)}>
              {item.label}
            </Link>
          ))}
        </div>
      </div>

      {recentPosts.length > 0 && (
        <div>
          <h6 className="text-white fw-bold mb-3">{t("Recent Post")}</h6>
          <div className="recent-post">
            {recentPosts.map((post) => (
              <Link
                key={post.slug}
                href={`/blog/${post.slug}`}
                className="post-item d-flex align-items-center text-decoration-none"
              >
                <img
                  src={post.image || "/assets/img/logotho.png"}
                  className="post-thumb me-2"
                  alt=""
                />
                <div className="post-title">{post.heading}</div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
